import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GatewayLogType, NotificationType, ToolType } from './utils/types';
import { DEFAULT_FONT_SIZE } from './utils/common/constants';
import { findLanguageIndex, fn_stripDoubleQuotes } from './utils/functions';
import { ServiceStatusParams } from './utils/events';

// types

// functions

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface AppState {
  //
  showNewSessionModal: {
    show: boolean;
    site_uuid?: string;
    service_uuid?: string;
    service_type?: string;
    tab?: string;
    stagingCatalog?: string;
  };
  // general:
  selectedTool: ToolType;
  expandedTool: ToolType;
  languageDropdownMenuDisplayed: boolean;
  language: string | null;
  fontSize: number;
  theme: string;
  userDropdownMenuDisplayed: boolean;
  languageIndex: number | undefined;
  // notebook tool:
  jupyterBaseURL: string;
  jupyterURL: string;
  jupyterRender: number;
  xsrfToken: string | null;
  loadingToken: boolean;
  notifications: NotificationType[];
  sitePanelFilter: string;
  projectPanelFilter: string;
  loadingSites: boolean;
  updateServiceStatus: ServiceStatusParams[];
  gatewayLogs: GatewayLogType[];
  loadingProjects: boolean;
  showPreferencesModal: boolean;
  showRenameFileModal: {
    show: boolean;
    filename?: string;
  };
  panelExpanded: boolean;
  //
} // AppState

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: AppState = {
  showNewSessionModal: { show: false },
  selectedTool: ToolType.HOME,
  expandedTool: ToolType.NONE,
  languageDropdownMenuDisplayed: false,
  language: localStorage.getItem('gateway_language'),
  fontSize: localStorage.getItem('font_size')
    ? Number(localStorage.getItem('font_size'))
    : DEFAULT_FONT_SIZE,
  theme: localStorage.getItem('gateway_theme') || 'auto',
  userDropdownMenuDisplayed: false,
  languageIndex: findLanguageIndex({
    language: fn_stripDoubleQuotes({
      value: localStorage.getItem('gateway_language'),
    }),
  }),
  // notebook tool:
  jupyterBaseURL: '',
  jupyterURL: '',
  jupyterRender: 0,
  xsrfToken: null,
  loadingToken: false,
  notifications: [],
  sitePanelFilter: '',
  projectPanelFilter: '',
  loadingSites: false,
  updateServiceStatus: [],
  gatewayLogs: [],
  loadingProjects: false,
  showPreferencesModal: false,
  showRenameFileModal: { show: false },
  panelExpanded: false,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const appSlice = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    //
    setShowNewSessionModal: (
      state: AppState,
      action: PayloadAction<{
        show: boolean;
        site_uuid?: string;
        service_uuid?: string;
        service_type?: string;
        tab?: string;
        stagingCatalog?: string;
      }>,
    ) => {
      state.showNewSessionModal = {
        show: action.payload.show,
        site_uuid: action.payload.site_uuid,
        service_uuid: action.payload.service_uuid,
        service_type: action.payload.service_type,
        tab: action.payload.tab,
        stagingCatalog: action.payload.stagingCatalog,
      };
    },

    setShowRenameFileModal: (
      state: AppState,
      action: PayloadAction<{
        show: boolean;
        filename?: string;
      }>,
    ) => {
      state.showRenameFileModal = {
        show: action.payload.show,
        filename: action.payload.filename,
      };
    },

    setSelectedTool: (state: AppState, action: PayloadAction<ToolType>) => {
      state.selectedTool = action.payload;
    },

    setExpandedTool: (state: AppState, action: PayloadAction<ToolType>) => {
      state.expandedTool = action.payload;
    },

    setLanguageDropdownMenuDisplayed: (
      state: AppState,
      action: PayloadAction<boolean>,
    ) => {
      state.languageDropdownMenuDisplayed = action.payload;
    },

    toggleLanguageDropdownMenuDisplayed: (state: AppState) => {
      state.languageDropdownMenuDisplayed = !state.languageDropdownMenuDisplayed;
    },

    setLanguage: (state: AppState, action: PayloadAction<string>) => {
      state.language = action.payload;
    },

    setFontSize: (state: AppState, action: PayloadAction<number>) => {
      state.fontSize = action.payload;
      localStorage.setItem('font_size', action.payload.toString());
    },

    setTheme: (state: AppState, action: PayloadAction<string>) => {
      state.theme = action.payload;
      localStorage.setItem('gateway_theme', action.payload);
    },

    setUserDropdownMenuDisplayed: (state: AppState, action: PayloadAction<boolean>) => {
      state.userDropdownMenuDisplayed = action.payload;
    },

    setLanguageIndex: (state, action: PayloadAction<number | undefined>) => {
      state.languageIndex =
        action.payload ??
        findLanguageIndex({
          language: fn_stripDoubleQuotes({
            value: localStorage.getItem('gateway_language'),
          }),
        });
    },

    // notebook tool:
    setJupyterBaseURL: (state: AppState, action: PayloadAction<string>) => {
      state.jupyterBaseURL = action.payload;
    },

    setJupyterURL: (state: AppState, action: PayloadAction<string>) => {
      state.jupyterURL = action.payload;
    },

    setJupyterRender: (state: AppState, action: PayloadAction<number>) => {
      state.jupyterRender = action.payload;
    },

    setXsrfToken: (state: AppState, action: PayloadAction<string | null>) => {
      state.xsrfToken = action.payload;
    },

    setLoadingToken: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingToken = action.payload;
    },

    setNotifications: (state: AppState, action: PayloadAction<NotificationType[]>) => {
      state.notifications = action.payload;
    },

    addNotification: (state: AppState, action: PayloadAction<NotificationType>) => {
      const existingIndex = state.notifications.findIndex(
        (n) => n.notificationID === action.payload.notificationID,
      );

      // Check that the notification is unique. This is required as we may receive a
      // notification via SSE that was already retrieved with a GET request.
      if (existingIndex === -1) {
        state.notifications.push(action.payload);
        // Ensure the notifications are sorted by date created (newest to oldest).
        state.notifications.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      }
    },

    addNotifications: (state: AppState, action: PayloadAction<NotificationType[]>) => {
      let doSort = false;

      for (var payloadNotification of action.payload) {
        const existingIndex = state.notifications.findIndex(
          (n) => n.notificationID === payloadNotification.notificationID,
        );

        // Check that the notification is unique. This is required as we may receive a
        // notification via SSE that was already retrieved with a GET request.
        if (existingIndex === -1) {
          doSort = true;
          state.notifications.push(payloadNotification);
        }
      }

      if (doSort) {
        // Ensure the notifications are sorted by date created (newest to oldest).
        state.notifications.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      }
    },

    markNotificationsRead: (state: AppState, action: PayloadAction<Number[]>) => {
      state.notifications.forEach((notification) => {
        if (action.payload.includes(notification.notificationID))
          notification.readFlag = true;
      });
    },

    clearNotificationPopupFlags: (state: AppState, action: PayloadAction<Number[]>) => {
      state.notifications.forEach((notification) => {
        if (action.payload.includes(notification.notificationID))
          notification.displayPopup = false;
      });
    },

    setSitePanelFilter: (state: AppState, action: PayloadAction<string>) => {
      state.sitePanelFilter = action.payload;
    },

    setProjectPanelFilter: (state: AppState, action: PayloadAction<string>) => {
      state.projectPanelFilter = action.payload;
    },

    setLoadingSites: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingSites = action.payload;
    },

    setUpdateServiceStatus: (
      state: AppState,
      action: PayloadAction<ServiceStatusParams[]>,
    ) => {
      state.updateServiceStatus = action.payload;
    },

    setGatewayLogs: (state: AppState, action: PayloadAction<GatewayLogType[]>) => {
      let reducedLogsMap: Map<string, GatewayLogType> = new Map<string, GatewayLogType>();
      // Note: This relies on the payload logs being in in descending timestamp order
      // with the newest log first to ensure that the latest duplicate's timestamp is
      // used in the actual entry.
      action.payload.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      for (let payloadLog of action.payload) {
        const reducedLog = reducedLogsMap.get(payloadLog.message);
        if (reducedLog === undefined) {
          payloadLog.metadata.repeat_count = 0;
          payloadLog.metadata.repeat_dates = [];
          reducedLogsMap.set(payloadLog.message, payloadLog);
        } else {
          if (reducedLog.metadata.repeat_count !== undefined)
            reducedLog.metadata.repeat_count++;
          reducedLog.metadata.repeat_dates?.push(payloadLog.created_at);
        }
      }
      let reducedLogs: GatewayLogType[] = Array.from(reducedLogsMap.values());
      state.gatewayLogs = reducedLogs;
    },

    prependGatewayLogs: (state: AppState, action: PayloadAction<GatewayLogType>) => {
      let isRepeat = false;
      for (const log of state.gatewayLogs) {
        if (action.payload.log_id === log.log_id) return;
        if (action.payload.message === log.message) {
          isRepeat = true;
          log.metadata.displayPopup = action.payload.metadata.displayPopup;
          if (
            log.metadata.repeat_count !== undefined &&
            log.metadata.repeat_dates !== undefined
          ) {
            log.metadata.repeat_count++;
            log.metadata.repeat_dates.push(log.created_at);
            log.created_at = action.payload.created_at;
          }
        }
      }
      if (!isRepeat) {
        action.payload.metadata.repeat_count = 0;
        action.payload.metadata.repeat_dates = [];
        state.gatewayLogs.unshift(action.payload);
      }
      state.gatewayLogs.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    },

    clearGatewayLogs(state: AppState) {
      state.gatewayLogs = [];
    },

    clearGatewayLogsPopupFlags: (state: AppState, action: PayloadAction<String[]>) => {
      state.gatewayLogs.forEach((log) => {
        if (action.payload.includes(log.log_id)) log.metadata.displayPopup = false;
      });
    },

    setLoadingProjects: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingProjects = action.payload;
    },

    setShowPreferencesModal: (state: AppState, action: PayloadAction<boolean>) => {
      state.showPreferencesModal = action.payload;
    },

    setPanelExpanded: (state: AppState, action: PayloadAction<boolean>) => {
      state.panelExpanded = action.payload;
    },
  } /*
  extraReducers: (builder) => {
    builder

      // run some code when the runService action finished, if you need to.
      .addCase(runService.fulfilled, (state: AppState) => {})
  },*/,
});

// expose the interface.
export const {
  setShowNewSessionModal,
  setShowRenameFileModal,
  setFontSize,
  setLanguage,
  setLanguageDropdownMenuDisplayed,
  toggleLanguageDropdownMenuDisplayed,
  setLanguageIndex,
  setSelectedTool,
  setExpandedTool,
  setTheme,
  setUserDropdownMenuDisplayed,
  setJupyterBaseURL,
  setJupyterRender,
  setJupyterURL,
  setXsrfToken,
  setLoadingToken,
  setShowPreferencesModal,
  setNotifications,
  addNotification,
  addNotifications,
  markNotificationsRead,
  clearNotificationPopupFlags,
  setSitePanelFilter,
  setProjectPanelFilter,
  setLoadingSites,
  setUpdateServiceStatus,
  setLoadingProjects,
  setGatewayLogs,
  prependGatewayLogs,
  clearGatewayLogs,
  clearGatewayLogsPopupFlags,
  setPanelExpanded,
} = appSlice.actions;
export default appSlice.reducer;
